import React, { useEffect, useState } from "react";
import {
  Segment,
  Header,
  Icon,
  Table,
  Button,
  Loader,
  Message,
  Form,
  TextArea,
  Label,
  Image,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { TeacherTasksAPI } from "../../../../api/teacherTasksAPI";
import { MozillaHubRoomsApi } from "../../../../api";
import { resolveSceneMeta } from "./sceneMeta";
import "./StudentTasksPanel.scss";

const tasksApi = new TeacherTasksAPI();
const hubRoomsApi = new MozillaHubRoomsApi();

/**
 * Props:
 * - accessToken
 * - studentId
 * - onStartQuiz: ({ teacherQuizId, taskId }) => void
 * - refreshKey?: number
 */
export function StudentTasksPanel({
  accessToken,
  studentId,
  onStartQuiz,
  refreshKey = 0,
}) {
  const { t } = useTranslation("students");
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);

  const [expandedTaskId, setExpandedTaskId] = useState(null);

  // { stepId, scene, instruction, ... } | { _endOfSteps: true }
  const [nextStep, setNextStep] = useState(null);
  // { title, bannerUrl, enterUrl, descriptionHtml }
  const [sceneMeta, setSceneMeta] = useState(null);
  const [sending, setSending] = useState(false);
  const [stepSent, setStepSent] = useState(false);

  const [mode, setMode] = useState("TEXT");
  const [text, setText] = useState("");

  const loadTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await tasksApi.getStudentTasks(accessToken, studentId);
      setTasks(Array.isArray(resp.tasks) ? resp.tasks : []);
    } catch (e) {
      setError(e?.message || t("tasks.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && studentId) loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, studentId, refreshKey]);

  const hydrateSceneMeta = async (stepLike) => {
    setSceneMeta(null);
    const meta = await resolveSceneMeta(accessToken, stepLike);
    setSceneMeta(meta);
  };

  const openTask = async (row) => {
    const taskObj = row?.task;
    const isSame = expandedTaskId === taskObj?._id;
    const newExpanded = isSame ? null : taskObj?._id;

    setExpandedTaskId(newExpanded);
    setNextStep(null);
    setSceneMeta(null);
    setMode("TEXT");
    setText("");
    setStepSent(false);

    if (!isSame && taskObj?._id) {
      try {
        const res = await tasksApi.getNextStep(
          accessToken,
          studentId,
          taskObj._id,
        );

        if (res?.next === null) {
          setNextStep({ _endOfSteps: true });
          return;
        }

        if (res?.next?.scene) {
          setNextStep(res.next);
          await hydrateSceneMeta(res.next);
        } else {
          setNextStep(null);
        }
      } catch (e) {
        setError(e?.message || t("tasks.nextStepError"));
      }
    }
  };

  const onToggleVR = () => {
    const nextMode = mode === "VR" ? "TEXT" : "VR";
    setMode(nextMode);
    setText(nextMode === "VR" ? t("tasks.vrCompleted") : "");
  };

  const canSubmit = mode === "VR" || text.trim().length > 0;

  const onSubmitAnswer = async () => {
    if (!nextStep?.stepId) return;
    if (!canSubmit) return;
    setSending(true);
    setStepSent(false);
    try {
      await tasksApi.submitStepAnswer(
        accessToken,
        studentId,
        expandedTaskId,
        nextStep.stepId,
        {
          mode,
          text: text || (mode === "VR" ? t("tasks.vrCompleted") : ""),
        },
      );

      setStepSent(true);
      setText("");
      setMode("TEXT");

      const res = await tasksApi.getNextStep(
        accessToken,
        studentId,
        expandedTaskId,
      );

      setStepSent(false);

      if (res?.next === null) {
        setNextStep({ _endOfSteps: true });

        const taskObj = tasks.find(
          (i) => i?.task?._id === expandedTaskId,
        )?.task;
        if (taskObj?.finalQuizRequired && taskObj?.finalQuiz) {
          onStartQuiz?.({
            teacherQuizId: taskObj.finalQuiz,
            taskId: taskObj._id,
          });
        }

        await loadTasks();
        return;
      }

      setNextStep(res.next);
      await hydrateSceneMeta(res.next);
    } catch (e) {
      setStepSent(false);
      setError(e?.message || t("tasks.submitError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Segment className="student-tasks-panel">
      <Header as="h3">
        <Icon name="tasks" />
        <Header.Content>{t("tasks.title")}</Header.Content>
      </Header>

      {loading ? (
        <Loader active inline="centered" />
      ) : error ? (
        <Message negative>
          <Message.Header>{t("tasks.errorTitle")}</Message.Header>
          <p>{error}</p>
        </Message>
      ) : tasks.length === 0 ? (
        <Message info>
          <Message.Header>{t("tasks.noTasksTitle")}</Message.Header>
          <p>{t("tasks.noTasksContent")}</p>
        </Message>
      ) : (
        <Table celled compact selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("tasks.taskHeader")}</Table.HeaderCell>
              <Table.HeaderCell>{t("tasks.progressHeader")}</Table.HeaderCell>
              <Table.HeaderCell>{t("tasks.statusHeader")}</Table.HeaderCell>
              <Table.HeaderCell>{t("tasks.quizHeader")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tasks.map((row) => {
              const taskObj = row.task || {};
              const expanded = expandedTaskId === taskObj._id;
              const quizRequired = !!taskObj.finalQuizRequired;
              const quizAssigned = !!taskObj.finalQuiz;

              return (
                <React.Fragment key={taskObj._id}>
                  <Table.Row onClick={() => openTask(row)} active={expanded}>
                    <Table.Cell>{taskObj.title}</Table.Cell>
                    <Table.Cell>
                      {Math.max(0, Math.min(100, row.progressPct || 0))}%
                    </Table.Cell>
                    <Table.Cell>{row.status || "-"}</Table.Cell>
                    <Table.Cell>
                      {quizRequired ? (
                        <Label color={quizAssigned ? "green" : "yellow"} basic>
                          {quizAssigned
                            ? t("tasks.required")
                            : t("tasks.requiredNotAssigned")}
                        </Label>
                      ) : (
                        <Label basic>—</Label>
                      )}
                    </Table.Cell>
                  </Table.Row>

                  {expanded && (
                    <Table.Row>
                      <Table.Cell colSpan="4">
                        {nextStep?._endOfSteps ? (
                          <>
                            {quizRequired && quizAssigned ? (
                              <Message positive>
                                <Message.Header>
                                  {t("tasks.allStepsCompleted")}
                                </Message.Header>
                                <Button
                                  color="violet"
                                  icon
                                  labelPosition="left"
                                  onClick={() =>
                                    onStartQuiz?.({
                                      teacherQuizId: taskObj.finalQuiz,
                                      taskId: taskObj._id,
                                    })
                                  }
                                >
                                  <Icon name="play" />
                                  {t("tasks.startFinalExam")}
                                </Button>
                              </Message>
                            ) : quizRequired && !quizAssigned ? (
                              <Message warning>
                                <Message.Header>
                                  {t("tasks.allStepsCompleted")}
                                </Message.Header>
                                {t("tasks.examNotAssigned")}
                              </Message>
                            ) : (
                              <Message positive>
                                <Message.Header>
                                  {t("tasks.taskCompleted")}
                                </Message.Header>
                                {t("tasks.noExamRequired")}
                              </Message>
                            )}
                          </>
                        ) : !nextStep ? (
                          <Loader active inline="centered">
                            {t("tasks.loadingStep")}
                          </Loader>
                        ) : (
                          <div className="task-step">
                            {/* Cabecera escena */}
                            <div className="task-step__scene">
                              {!sceneMeta ? (
                                <Loader active inline="centered" />
                              ) : (
                                <>
                                  {sceneMeta.bannerUrl ? (
                                    <Image
                                      src={sceneMeta.bannerUrl}
                                      alt="banner scene"
                                      bordered
                                    />
                                  ) : null}
                                  <Header as="h4" className="task-step__title">
                                    {sceneMeta.title ||
                                      t("tasks.sceneFallback")}
                                  </Header>
                                  {/* Acciones de sala */}
                                  <div className="task-step__scene-actions">
                                    {sceneMeta.enterUrl ? (
                                      <Button
                                        as="a"
                                        href={sceneMeta.enterUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        color="blue"
                                        icon
                                        labelPosition="left"
                                        onClick={() =>
                                          hubRoomsApi.trackRoomOpen(
                                            accessToken,
                                            {
                                              sceneRefId: nextStep?.scene,
                                              url: sceneMeta.enterUrl,
                                              title: sceneMeta.title,
                                              source: "task_student",
                                            },
                                          )
                                        }
                                      >
                                        <Icon name="sign-in" />
                                        {t("portal.enterButton")}
                                      </Button>
                                    ) : null}
                                    <Button
                                      toggle
                                      active={mode === "VR"}
                                      onClick={onToggleVR}
                                      icon
                                      labelPosition="left"
                                    >
                                      <Icon name="cube" />
                                      {mode === "VR"
                                        ? t("tasks.vrModeActive")
                                        : t("tasks.vrToggle")}
                                    </Button>
                                  </div>
                                  {sceneMeta.descriptionHtml ? (
                                    <div
                                      className="task-step__scene-description"
                                      style={{ marginTop: 12 }}
                                      dangerouslySetInnerHTML={{
                                        __html: sceneMeta.descriptionHtml,
                                      }}
                                    />
                                  ) : null}
                                </>
                              )}
                            </div>

                            {/* Instruccion del paso */}
                            <div className="task-step__instruction">
                              <Header as="h5" style={{ marginBottom: 6 }}>
                                {t("tasks.activityDescription")}
                              </Header>
                              <p>
                                {nextStep?.instruction ||
                                  t("tasks.noDescription")}
                              </p>
                            </div>

                            {/* Respuesta */}
                            <div className="task-step__answer">
                              {stepSent && (
                                <Message positive compact>
                                  <Icon name="check circle" />
                                  {t("tasks.stepSent")}
                                </Message>
                              )}

                              {mode !== "VR" && (
                                <Form
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    onSubmitAnswer();
                                  }}
                                >
                                  <Form.Field>
                                    <label>{t("tasks.yourAnswer")}</label>
                                    <TextArea
                                      value={text}
                                      onChange={(_, { value }) =>
                                        setText(value)
                                      }
                                      placeholder={t("tasks.writePlaceholder")}
                                    />
                                    {!canSubmit && text !== "" && (
                                      <small style={{ color: "#e00" }}>
                                        {t("tasks.answerEmpty")}
                                      </small>
                                    )}
                                  </Form.Field>
                                  <Button
                                    primary
                                    loading={sending}
                                    disabled={sending || !canSubmit}
                                    icon
                                    labelPosition="left"
                                    type="submit"
                                  >
                                    <Icon name="send" />
                                    {t("tasks.sendButton")}
                                  </Button>
                                </Form>
                              )}

                              {mode === "VR" && (
                                <div className="task-step__vr-confirm">
                                  <Message info compact>
                                    <Icon name="cube" />
                                    {t("tasks.vrCompleted")}
                                  </Message>
                                  <Button
                                    primary
                                    loading={sending}
                                    disabled={sending}
                                    icon
                                    labelPosition="left"
                                    onClick={onSubmitAnswer}
                                  >
                                    <Icon name="send" />
                                    {t("tasks.sendButton")}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Segment>
  );
}
